import { c as _c } from "react/compiler-runtime";
import type { VariantProps } from "class-variance-authority";
import { Kbd } from "@/components/ui/Kbd";
import { menuContentVariants, menuItemIndicatorClassName, menuItemVariants, menuLabelClassName, menuSeparatorClassName, menuShortcutClassName } from "@/components/ui/menu-surface";
import { useOrderedOverlayContainer } from "@/components/ui/ordered-overlay";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { Check, ChevronRight } from "lucide-react";
import * as React from "react";
import cn from "twsx";

/** Use when a trigger opens a menu of actions or choices. */
const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = t0 => {
  const $ = _c(6);
  let container;
  let props;
  if ($[0] !== t0) {
    ({
      container,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = container;
    $[2] = props;
  } else {
    container = $[1];
    props = $[2];
  }
  const orderedContainer = useOrderedOverlayContainer();
  const t1 = container ?? orderedContainer ?? undefined;
  let t2;
  if ($[3] !== props || $[4] !== t1) {
    t2 = <DropdownMenuPrimitive.Portal container={t1} {...props} />;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
const DropdownMenuSubTrigger = t0 => {
  const $ = _c(22);
  let asChild;
  let children;
  let className;
  let destructive;
  let inset;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      children,
      destructive,
      asChild,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = children;
    $[3] = className;
    $[4] = destructive;
    $[5] = inset;
    $[6] = props;
    $[7] = ref;
  } else {
    asChild = $[1];
    children = $[2];
    className = $[3];
    destructive = $[4];
    inset = $[5];
    props = $[6];
    ref = $[7];
  }
  let t1;
  if ($[8] !== asChild || $[9] !== className || $[10] !== destructive || $[11] !== inset) {
    t1 = asChild ? className : cn(menuItemVariants({
      inset,
      destructive,
      subTrigger: true
    }), className);
    $[8] = asChild;
    $[9] = className;
    $[10] = destructive;
    $[11] = inset;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] !== asChild || $[14] !== children) {
    t2 = asChild ? children : <>{children}<ChevronRight className="ml-auto !size-3 !text-text-tertiary" /></>;
    $[13] = asChild;
    $[14] = children;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== asChild || $[17] !== props || $[18] !== ref || $[19] !== t1 || $[20] !== t2) {
    t3 = <DropdownMenuPrimitive.SubTrigger ref={ref} asChild={asChild} className={t1} {...props}>{t2}</DropdownMenuPrimitive.SubTrigger>;
    $[16] = asChild;
    $[17] = props;
    $[18] = ref;
    $[19] = t1;
    $[20] = t2;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  return t3;
};
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
const DropdownMenuSubContent = t0 => {
  const $ = _c(12);
  let className;
  let padding;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      padding,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = padding;
    $[3] = props;
    $[4] = ref;
  } else {
    className = $[1];
    padding = $[2];
    props = $[3];
    ref = $[4];
  }
  let t1;
  if ($[5] !== className || $[6] !== padding) {
    t1 = cn(menuContentVariants({
      padding
    }), "max-h-[--radix-dropdown-menu-content-available-height]", className);
    $[5] = className;
    $[6] = padding;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== props || $[9] !== ref || $[10] !== t1) {
    t2 = <DropdownMenuPrimitive.SubContent ref={ref} className={t1} {...props} />;
    $[8] = props;
    $[9] = ref;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
const DropdownMenuContent = t0 => {
  const $ = _c(14);
  let className;
  let padding;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      padding,
      sideOffset: t1,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = padding;
    $[3] = props;
    $[4] = ref;
    $[5] = t1;
  } else {
    className = $[1];
    padding = $[2];
    props = $[3];
    ref = $[4];
    t1 = $[5];
  }
  const sideOffset = t1 === undefined ? 4 : t1;
  let t2;
  if ($[6] !== className || $[7] !== padding) {
    t2 = cn(menuContentVariants({
      padding
    }), "max-h-[--radix-dropdown-menu-content-available-height]", className);
    $[6] = className;
    $[7] = padding;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== props || $[10] !== ref || $[11] !== sideOffset || $[12] !== t2) {
    t3 = <DropdownMenuPortal><DropdownMenuPrimitive.Content ref={ref} sideOffset={sideOffset} className={t2} {...props} /></DropdownMenuPortal>;
    $[9] = props;
    $[10] = ref;
    $[11] = sideOffset;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
DropdownMenuTrigger.displayName = DropdownMenuPrimitive.Trigger.displayName;
const DropdownMenuItem = t0 => {
  const $ = _c(16);
  let className;
  let destructive;
  let inset;
  let props;
  let ref;
  let size;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      destructive,
      size,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = destructive;
    $[3] = inset;
    $[4] = props;
    $[5] = ref;
    $[6] = size;
  } else {
    className = $[1];
    destructive = $[2];
    inset = $[3];
    props = $[4];
    ref = $[5];
    size = $[6];
  }
  let t1;
  if ($[7] !== className || $[8] !== destructive || $[9] !== inset || $[10] !== size) {
    t1 = cn(menuItemVariants({
      inset,
      destructive,
      size
    }), className);
    $[7] = className;
    $[8] = destructive;
    $[9] = inset;
    $[10] = size;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== props || $[13] !== ref || $[14] !== t1) {
    t2 = <DropdownMenuPrimitive.Item ref={ref} className={t1} {...props} />;
    $[12] = props;
    $[13] = ref;
    $[14] = t1;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
};
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
const DropdownMenuCheckboxItem = t0 => {
  const $ = _c(17);
  let checked;
  let children;
  let className;
  let props;
  let ref;
  let size;
  if ($[0] !== t0) {
    ({
      className,
      children,
      checked,
      size,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = checked;
    $[2] = children;
    $[3] = className;
    $[4] = props;
    $[5] = ref;
    $[6] = size;
  } else {
    checked = $[1];
    children = $[2];
    className = $[3];
    props = $[4];
    ref = $[5];
    size = $[6];
  }
  let t1;
  if ($[7] !== className || $[8] !== size) {
    t1 = cn(menuItemVariants({
      indicator: true,
      size
    }), className);
    $[7] = className;
    $[8] = size;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className={menuItemIndicatorClassName}><DropdownMenuPrimitive.ItemIndicator><Check /></DropdownMenuPrimitive.ItemIndicator></span>;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== checked || $[12] !== children || $[13] !== props || $[14] !== ref || $[15] !== t1) {
    t3 = <DropdownMenuPrimitive.CheckboxItem ref={ref} className={t1} checked={checked} {...props}>{t2}{children}</DropdownMenuPrimitive.CheckboxItem>;
    $[11] = checked;
    $[12] = children;
    $[13] = props;
    $[14] = ref;
    $[15] = t1;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
};
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;
const DropdownMenuRadioItem = t0 => {
  const $ = _c(13);
  let children;
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = ref;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    ref = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn(menuItemVariants({
      indicator: true
    }), className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className={menuItemIndicatorClassName}><DropdownMenuPrimitive.ItemIndicator><Check /></DropdownMenuPrimitive.ItemIndicator></span>;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== props || $[10] !== ref || $[11] !== t1) {
    t3 = <DropdownMenuPrimitive.RadioItem ref={ref} className={t1} {...props}>{t2}{children}</DropdownMenuPrimitive.RadioItem>;
    $[8] = children;
    $[9] = props;
    $[10] = ref;
    $[11] = t1;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
const DropdownMenuLabel = t0 => {
  const $ = _c(12);
  let className;
  let inset;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = inset;
    $[3] = props;
    $[4] = ref;
  } else {
    className = $[1];
    inset = $[2];
    props = $[3];
    ref = $[4];
  }
  const t1 = inset && "pl-8";
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = cn(menuLabelClassName, t1, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props || $[9] !== ref || $[10] !== t2) {
    t3 = <DropdownMenuPrimitive.Label ref={ref} className={t2} {...props} />;
    $[8] = props;
    $[9] = ref;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
const DropdownMenuSeparator = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn(menuSeparatorClassName, className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <DropdownMenuPrimitive.Separator ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
const DropdownMenuShortcut = t0 => {
  const $ = _c(5);
  const {
    keys,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(menuShortcutClassName, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== keys || $[3] !== t1) {
    t2 = <Kbd keys={keys} variant="inline" className={t1} />;
    $[2] = keys;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";
export { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger };